import { useIsEnabled } from '@troon/analytics';
import { For, Show, Suspense } from 'solid-js';
import { createAsync } from '@solidjs/router';
import {
	AccessPremiumLogo,
	ActivityIndicator,
	Button,
	CheckList,
	CheckListItem,
	Container,
	Dialog,
	Heading,
	Hero,
	HGroup,
	HorizontalRule,
	Link,
	MediaSection,
	Page,
	Picture,
	ProductCardV2,
	Section,
	Tabs,
} from '@troon/ui';
import { IconShoppingCart01 } from '@troon/icons/shopping-cart-01';
import { IconTag } from '@troon/icons/tag';
import { IconStar } from '@troon/icons/star';
import { cachedGet } from '@troon/api-client';
import { IconChevronDown } from '@troon/icons/chevron-down';
import { IconInfo } from '@troon/icons/info';
import { twJoin } from '@troon/tailwind-preset/merge';
import { getConfigValue } from '../../../modules/config';
import { FacilitiesByGrouping } from '../../../components/facility/by-grouping';
import { FacilityCard } from '../../../components/facility/card';
import { SubscriptionProducts } from '../../../components/subscription-products';
import { AccessShopDiscountDetails, PlanComparison } from '../../../components/plan-comparison';
import { TopoBg } from '../../../components/topo-bg';
import { FrequentlyAskedQuestions } from '../../../components/faqs';
import { useUser } from '../../../providers/user';
import { useAccessPremiumEliteEnabled } from '../../../modules/access-upgrades';
import { AccessLanding } from './_oldindex';
import type { RouteDefinition } from '@solidjs/router';

export default function MembershipPage() {
	const isEnabled = useIsEnabled('elite-split-pages');
	const user = useUser();
	const eliteEnabled = useAccessPremiumEliteEnabled();

	return (
		<Show when={isEnabled} fallback={<AccessLanding />}>
			<Hero src={`${getConfigValue('IMAGE_HOST')}/membership/hero.png`} fullWidth straight overlay="left-right">
				<Container class="w-full items-start gap-8 py-8 text-start">
					<HGroup>
						<HGroup.Heading as="h1">
							<AccessPremiumLogo class="h-10 max-w-56 md:max-w-64" color="current" />{' '}
							<span class="sr-only">Access Premium</span>
						</HGroup.Heading>
						<HGroup.Content size="h1" class="flex flex-col md:text-6xl">
							<span>Member Savings.</span>
							<span class="font-normal">On & Off The Course.</span>
						</HGroup.Content>
					</HGroup>

					<p class="max-w-4xl text-sm text-balance md:text-base">
						Unlock exclusive rates at 200+ top courses. Members save on every round, every Access Shop order, and
						everything in between.
					</p>
					<Show
						when={user()?.subscription}
						fallback={
							<Button as={Link} href="#join">
								Join Access Premium
							</Button>
						}
					>
						<Button as={Link} href="/account/subscription">
							Manage subscription
						</Button>
					</Show>
				</Container>
			</Hero>

			<Page class="pt-0">
				<Container>
					<Page>
						<Section class="grid gap-8 gap-x-16 text-sm text-balance lg:grid-cols-3">
							<div class="flex items-start gap-4">
								<div>
									<IconTag class="size-12 text-brand" />
								</div>
								<div>
									<Heading as="h2" size="h5">
										Tee Time Savings
									</Heading>
									<p>Save 15% on every tee time and up to 50% off inside 78 hr booking window.</p>
								</div>
							</div>

							<div class="flex items-start gap-4">
								<div>
									<IconShoppingCart01 class="size-12 text-brand" />
								</div>
								<div>
									<Heading as="h2" size="h5">
										Access Shop Savings
									</Heading>
									<p>Get 10-15% back in store credit on every Access Shop order.</p>
								</div>
							</div>

							<div class="flex items-start gap-4">
								<div>
									<IconStar class="size-12 text-brand" />
								</div>
								<div>
									<Heading as="h2" size="h5">
										Instant Loyalty Status
									</Heading>
									<p>Earn points faster with automatic Silver, Gold or Platinum status.</p>
								</div>
							</div>
						</Section>

						<HorizontalRule />

						<Section>
							<div class="flex flex-col gap-4">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Play
									</HGroup.Heading>
									<HGroup.Content size="h2">
										Premium courses. <span class="font-normal">Member rates.</span>
									</HGroup.Content>
								</HGroup>
								<p>
									Premium members get 15% off every tee time, and up to 50% off inside the 78 hour booking window with
									Premium Plus<Show when={eliteEnabled()}> or Premium Elite</Show>.
								</p>
							</div>

							<Tabs defaultValue="featured" lazyMount>
								<Tabs.List aria-label="Access course lists">
									<Tabs.Tab value="featured">Featured Courses</Tabs.Tab>
									<Tabs.Tab value="nearby">Nearby Courses</Tabs.Tab>
									<Tabs.Tab value="all">All Courses</Tabs.Tab>
								</Tabs.List>

								<Tabs.Panel value="featured">
									<Facilities type="featured" />
								</Tabs.Panel>
								<Tabs.Panel value="nearby">
									<Facilities type="nearby" />
								</Tabs.Panel>
								<Tabs.Panel value="all">
									<AllFacilities />
								</Tabs.Panel>
							</Tabs>
						</Section>

						<HorizontalRule />

						<MediaSection src={`${getConfigValue('IMAGE_HOST')}/membership/shop.png`} aspectRatio="square">
							<div class="flex flex-col gap-4">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Shop
									</HGroup.Heading>
									<HGroup.Content size="h2">
										Your favorite brands. <span class="font-normal">Member-only savings.</span>
									</HGroup.Content>
								</HGroup>
								<p>Shop a curated collection of golf's top brands and enjoy exclusive member benefits and savings.</p>
								<CheckList>
									<CheckListItem>
										Earn 10-15% back in Access Shop credit on all eligible Access Shop orders.
									</CheckListItem>
									<CheckListItem>
										<p>Annual Access Shop credits & discounts</p>
										<ul class="flex list-disc flex-col gap-2 ps-4 font-light">
											<li>Premium - up to $100 in Access shop discount</li>
											<li>Premium Plus - up to $150 in Access Shop discount</li>
											<Show when={eliteEnabled()}>
												<li>
													<p class="flex items-center gap-2">
														Premium Elite - up to $500 Access Shop discount{' '}
														<Dialog key="elite-shop-credit-details">
															<Dialog.Trigger class="cursor-point">
																<IconInfo class="text-lg text-brand-600" />
																<span class="sr-only">More info</span>
															</Dialog.Trigger>
															<Dialog.Container>
																<Dialog.Title>Access Shop Discount</Dialog.Title>
																<Dialog.Content>
																	<AccessShopDiscountDetails />
																</Dialog.Content>
																<Dialog.Actions>
																	{(close) => <Dialog.Action onClick={close}>Okay</Dialog.Action>}
																</Dialog.Actions>
															</Dialog.Container>
														</Dialog>
													</p>
												</li>
											</Show>
										</ul>
									</CheckListItem>
								</CheckList>
								<Button as={Link} appearance="tertiary-current" href="https://shop.troon.com" class="w-fit">
									Visit the shop
								</Button>
							</div>
						</MediaSection>

						<HorizontalRule />

						<MediaSection src={`${getConfigValue('IMAGE_HOST')}/membership/loyalty.png`} aspectRatio="square" reverse>
							<div class="flex flex-col gap-4">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Earn
									</HGroup.Heading>
									<HGroup.Content size="h2">
										Earn points. <span class="font-normal">Get rewarded.</span>
									</HGroup.Content>
								</HGroup>
								<p>
									Every tee time at participating courses, and every eligible Access Shop purchase earns you points.
									Redeem them for rounds, gear, or shop credit. Premium members earn points faster with automatic
									Silver, Gold or Platinum status (1.1x, 1.15x, 1.2x point multiplier).
								</p>
								<CheckList>
									<CheckListItem>50+ participating courses in the network</CheckListItem>
									<CheckListItem>Seamless redemption online or in mobile app</CheckListItem>
								</CheckList>
								<Button as={Link} appearance="tertiary-current" href="/loyalty" class="w-fit">
									More about Access Loyalty
								</Button>
							</div>
						</MediaSection>

						<HorizontalRule />

						<MediaSection src={`${getConfigValue('IMAGE_HOST')}/membership/guest-passes.png`} aspectRatio="square">
							<div class="flex flex-col gap-4">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Share
									</HGroup.Heading>
									<HGroup.Content size="h2">
										Guest passes. <span class="block font-normal">Bring your whole foursome.</span>
									</HGroup.Content>
								</HGroup>
								<p>Guest Passes allow your playing partners to save and play at your Premium rate.</p>
								<CheckList>
									<CheckListItem>Premium: 3 Guest Passes / year</CheckListItem>
									<CheckListItem>Premium Plus: 6 Guest Passes / year</CheckListItem>
									<Show when={eliteEnabled()}>
										<CheckListItem>
											<p>
												Premium Elite: <strong class="text-brand-600">Unlimited Guest Passes</strong>
											</p>
										</CheckListItem>
									</Show>
								</CheckList>
							</div>
						</MediaSection>
					</Page>
				</Container>

				<Section appearance="secondary" id="join">
					<Container>
						<Container size="sm">
							<div class="flex w-full flex-col items-center gap-4 text-center">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Membership
									</HGroup.Heading>
									<HGroup.Content size="h2">Choose Your Access</HGroup.Content>
								</HGroup>
								<p>Pick the plan that best fits how you play and how you shop.</p>
							</div>
						</Container>

						<div
							class={twJoin(
								'grid grid-cols-1 gap-8 lg:flex-row-reverse',
								eliteEnabled() ? 'lg:grid-cols-3' : 'mx-auto lg:grid-cols-2',
							)}
						>
							<SubscriptionProducts cardComponent={ProductCardV2} />
						</div>

						<details class="group flex flex-col gap-8">
							<Button as="summary" appearance="secondary" class="mx-auto size-fit">
								Compare all plans & Benefits <IconChevronDown class="transition-transform group-open:rotate-180" />
							</Button>
							<PlanComparison />
						</details>

						<div class="relative flex w-full flex-wrap gap-4 rounded-lg bg-linear-to-r from-brand-900 to-brand-700 p-6 text-balance text-white sm:items-center sm:justify-between md:flex-nowrap md:p-8">
							<TopoBg placement="bottom" class="absolute inset-x-0 bottom-0 z-0 h-64 w-full text-white/50 md:h-32" />
							<div class="relative z-10 flex grow flex-col gap-8">
								<div class="flex flex-col gap-4">
									<Heading as="h2" size="h4">
										The perfect gift for the golfer in your life
									</Heading>
									<p>
										With savings at over 200 courses, a Premium membership is ultimate gift for the golfer in your life.
									</p>
								</div>
								<Button
									as={Link}
									href="/membership/gift"
									class="size-fit grow-0 after:absolute after:inset-0"
									size="sm"
								>
									Gift Access Premium
								</Button>
							</div>
							<Picture
								src={`${getConfigValue('IMAGE_HOST')}/hero/premium-cards-shadow.png`}
								width={1188}
								height={907}
								sizes="(min-width: 768px) 12rem, 50vw"
								alt=""
								class="relative z-10 order-first w-2/3 grow-0 self-start md:order-2 md:w-auto md:max-w-fit"
							/>
						</div>
					</Container>
				</Section>

				<Section>
					<Container size="xl">
						<div class="flex w-full flex-col items-center gap-4 text-center">
							<Heading as="h2" size="h2" class="text-center">
								Questions before you join?
							</Heading>
							<p>We've answered the most common ones below.</p>
						</div>
						<div class="overflow-hidden rounded-lg border border-neutral">
							<FrequentlyAskedQuestions id="membership-faqs" />
						</div>
					</Container>
				</Section>
			</Page>
		</Show>
	);
}

function Facilities(props: { type: 'featured' | 'nearby' }) {
	const facilities = createAsync(
		async () => {
			const query =
				props.type === 'featured'
					? { featuredOnGolfWithAccess: true, supportsTroonAccess: true, limit: 3 }
					: { nearby: true, supportsTroonAccess: true, limit: 3 };
			const res = await getFacilities({ query });
			return res.facilities;
		},
		{ deferStream: false },
	);

	return (
		<div class="grid gap-8 md:grid-cols-3">
			<Suspense
				fallback={
					<div class="col-span-3 grid h-32 place-items-center">
						<ActivityIndicator />
					</div>
				}
			>
				<For
					each={facilities()}
					fallback={
						<p class="col-span-3 py-16 text-center">Sorry, we do not have any courses near you at this time.</p>
					}
				>
					{(facility) => <FacilityCard facility={facility} level="h3" loading="lazy" linkToTeeTimes disableFavorites />}
				</For>
			</Suspense>
		</div>
	);
}

function AllFacilities() {
	const facilities = createAsync(
		async () => {
			const all = await getFacilities({ query: { isBookable: true, sortBy: 'name', supportsTroonAccess: true } });

			return all.facilities;
		},
		{ deferStream: true },
	);

	return (
		<Suspense
			fallback={
				<div class="col-span-3 grid h-32 place-items-center">
					<ActivityIndicator />
				</div>
			}
		>
			<Show when={facilities()}>{(facilities) => <FacilitiesByGrouping facilities={facilities()} />}</Show>
		</Suspense>
	);
}

const getFacilities = cachedGet('/v0/facilities');

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
